import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, TableColumnsType, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilSimple, Trash } from '@phosphor-icons/react';

import { WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { Product, getProducts } from '@hooks';
import { ProductFormModal } from './ProductFormModal';
import { DeleteProductModal } from './DeleteProductModal';

const { Title, Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

export const ProductsManagementPage = () => {
  const navigate = useNavigate();
  
  // States to control modals
  const [productFormModalOpen, setProductFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // States for pagination and search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  // Parameters for product search
  const params = {
    page,
    limit,
    search,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch products using the hook
  const { data, isLoading, refetch } = getProducts(params);

  // Refetch when params change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, refetch]);

  // Check if data exists before accessing properties
  const products = data?.data || [];
  const total = data?.pagination?.totalCount || 0;

  // Open modal to add a new product
  const openAddProductModal = () => {
    setSelectedProduct(null);
    setProductFormModalOpen(true);
  };

  // Open modal to edit a product
  const openEditProductModal = (product: Product) => {
    setSelectedProduct(product);
    setProductFormModalOpen(true);
  };

  // Open modal to delete a product
  const openDeleteProductModal = (product: Product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Handle table changes (pagination, sorting)
  const handleTableChange = (pagination: any, _filters: unknown, sorter: any) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current);
      return;
    }

    if (sorter.columnKey !== sortField || sorter.order !== sortOrder) {
      setSortField(sorter.columnKey as string);
      setSortOrder(sorter.order as string);
      setPage(1);
    }
  };

  // Helper function to get product image URL
  const getProductImage = (product: Product) => {
    if (product.images && product.images.length > 0) {
      const imageUrl = product.images[0];
      if (imageUrl.startsWith('http')) {
        return imageUrl;
      }
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${imageUrl}`;
    }
    if (product.imageUrl) {
      if (product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    return 'https://placehold.co/100x100?text=No+Image';
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Table columns configuration
  const columns: TableColumnsType<Product> = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (_, record) => (
        <img 
          src={getProductImage(record)} 
          alt={record.name}
          className="w-12 h-12 object-cover rounded"
          onError={(e) => {
            e.currentTarget.src = 'https://placehold.co/100x100?text=No+Image';
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string) => (
        <span className="font-['Barlow'] text-white">{text}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <span className="font-['Barlow'] text-white">
          {text && text.length > 100 ? `${text.substring(0, 100)}...` : text || '-'}
        </span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <span className="font-['Barlow'] text-white">
          {category || '-'}
        </span>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'variants',
      key: 'price',
      sorter: true,
      render: (variants: any) => {
        if (!variants || variants.length === 0) {
          return (
            <span className="font-['Barlow'] text-yellow-principal">
              -
            </span>
          );
        }
        
        const prices = variants.map((variant: any) => variant.price);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        
        if (minPrice === maxPrice) {
          return (
            <span className="font-['Barlow'] text-yellow-principal">
              {formatPrice(minPrice)}
            </span>
          );
        }
        
        return (
          <span className="font-['Barlow'] text-yellow-principal">
            {formatPrice(minPrice)} - {formatPrice(maxPrice)}
          </span>
        );
      },
    },
    {
      title: 'Stock',
      dataIndex: 'variants',
      key: 'stockQuantity',
      sorter: true,
      width: 100,
      render: (variants: any) => {
        let totalStock = 0;
        
        if (variants && variants.length > 0) {
          totalStock = variants.reduce((sum: number, variant: any) => sum + (variant.stockQuantity || 0), 0);
        }
        
        let colorClass = 'text-green-500'; // More than 10
        if (totalStock === 0) {
          colorClass = 'text-red-500';
        } else if (totalStock <= 10) {
          colorClass = 'text-yellow-principal';
        }
        
        return (
          <div className="flex flex-col items-center">
            <span className={`font-['Barlow'] ${colorClass}`}>
              {totalStock}
            </span>
            {totalStock === 0 && (
              <span className="font-['Barlow'] text-red-500 text-xs">
                OUT OF STOCK
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      render: (date: string) => (
        <span className="font-['Barlow'] text-white">
          {dayjs(date).format('MMM DD, YYYY')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className="flex gap-3">
          <Tooltip title="View details">
            <Button
              icon={<EyeOutlined />}
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/merch/${record.id}`);
              }}
              className="!text-blue-400 hover:!text-blue-300 !bg-transparent hover:!bg-transparent [&:hover_svg]:!scale-110 transition-all duration-200"
            />
          </Tooltip>
          <Tooltip title="Edit product">
            <Button
              icon={<PencilSimple size={16} />}
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                openEditProductModal(record);
              }}
              className="!text-[#E3AF25] [&_svg]:!text-[#E3AF25] [&_svg]:!fill-[#E3AF25] [&:hover_svg]:transition-all [&:hover_svg]:duration-200 [&:hover_svg]:drop-shadow-[0_0_8px_#E3AF25]"
            />
          </Tooltip>
          <Tooltip title="Delete product">
            <Button
              icon={<Trash size={16} />}
              type="text"
              danger
              onClick={(e) => {
                e.stopPropagation();
                openDeleteProductModal(record);
              }}
              className="!text-red-500 [&_svg]:!text-red-500 [&_svg]:!fill-red-500 [&:hover_svg]:transition-all [&:hover_svg]:duration-200 [&:hover_svg]:drop-shadow-[0_0_8px_#ef4444]"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="careers-bg opacity-40 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-white text-center">
            PRODUCTS MANAGEMENT
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Manage your product catalog - add, edit, or remove products
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full py-15 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6 gap-4">
            <Title level={2} className="text-yellow-principal font-android !font-normal m-0">PRODUCTS</Title>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              size="large"
              onClick={openAddProductModal}
              className="bg-yellow-principal !text-black font-android text-lg hover-3d"
            >
              ADD PRODUCT
            </Button>
          </div>

          <Paragraph className="text-gray-300 font-barlow mb-8">
            Manage your product catalog - add, edit, or remove products
          </Paragraph>

          <WrapperPage isLoading={isLoading}>
            <GenericList<Product>
              columns={columns}
              dataSource={products}
              onRow={(record) => ({
                onClick: () => openEditProductModal(record),
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for adding/editing product */}
          <ProductFormModal
            open={productFormModalOpen}
            onCancel={() => setProductFormModalOpen(false)}
            editingProduct={selectedProduct}
            onSuccess={() => {
              setProductFormModalOpen(false);
              refetch();
            }}
          />

          {/* Modal to confirm deletion */}
          <DeleteProductModal
            open={deleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            product={selectedProduct}
            onSuccess={() => {
              setDeleteModalOpen(false);
              refetch();
            }}
          />
        </div>
      </section>
    </div>
  );
};
